import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Alert,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";

import { useAuth } from "@/providers";
import { RobotoTooltip } from "@/shared/components";
import { AlertDialog } from "@/shared/components/AlertDialog";
import { assignTagColor } from "@/shared/components/TagList/helpers";
import {
  SkillAccessibility,
  SkillListScope,
  SkillSummary,
} from "@/shared/domain/skills";
import {
  useDeleteSkill,
  useDeleteVersion,
  useSetSubscription,
  useSkills,
  useSkillVersions,
  useSubscribeSkill,
  useUnsubscribeSkill,
  useUpdateSkillMetadata,
} from "@/shared/domain/skills/hooks";
import { Button } from "@/shared/foundation/primitives";
import {
  DataTable,
  useDataTableState,
  type ColDef,
  type DataTableLoadingState,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";

import { SkillEditor } from "./SkillEditor";
import { SkillInspector } from "./SkillInspector";
import { SkillMetadataEditor } from "./SkillMetadataEditor";
import styles from "./Skills.module.css";
import { TagsEditor } from "./TagsEditor";

interface EditorTarget {
  summary: SkillSummary;
  version: number | null;
}

/** Active filter chip applied above the skills table. */
type SkillFilter =
  | { kind: "text"; value: string }
  | { kind: "tag"; value: string };

function filterKey(f: SkillFilter): string {
  // Used for dedupe + React key. Case-insensitive so "QA" and "qa" don't
  // both end up in the chip strip.
  return `${f.kind}:${f.value.toLowerCase()}`;
}

interface VersionDeleteTarget {
  summary: SkillSummary;
  version: number;
  isLastVersion: boolean;
}

export function Skills() {
  const { currentOrganization, currentUser } = useAuth();
  const orgId = currentOrganization?.org_id;
  const userId = currentUser?.user_id ?? "";

  const [tab, setTab] = React.useState<SkillListScope>("personal");
  const skillsQuery = useSkills(orgId, tab);
  const deleteSkill = useDeleteSkill(orgId);
  const updateMetadata = useUpdateSkillMetadata(orgId);
  const subscribeSkill = useSubscribeSkill(orgId);
  const unsubscribeSkill = useUnsubscribeSkill(orgId);
  const setSubscription = useSetSubscription(orgId);

  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editingTarget, setEditingTarget] = React.useState<EditorTarget | null>(
    null,
  );
  const [inspectTarget, setInspectTarget] = React.useState<{
    summary: SkillSummary;
    version: number | null;
  } | null>(null);
  // Track which skill rows have an in-flight subscription mutation so the
  // toggle / button on that row spins individually instead of disabling the
  // whole tab.
  const [pendingSubscriptionIds, setPendingSubscriptionIds] = React.useState<
    Set<string>
  >(() => new Set());
  const [pendingAiVersionIds, setPendingAiVersionIds] = React.useState<
    Set<string>
  >(() => new Set());
  // Edit-skill-metadata is a separate dialog from the version editor — name,
  // accessibility, and tags belong to the skill row, not to any single
  // version. Splitting them keeps users from wondering whether a tag they
  // changed will land on "this version" or "the whole skill."
  const [metadataEditTarget, setMetadataEditTarget] =
    React.useState<SkillSummary | null>(null);
  // Tags-only modal opened from the inline double-click affordance on the
  // Tags column. Separate from the metadata editor so the user gets exactly
  // the field they clicked into — nothing else.
  const [tagsEditTarget, setTagsEditTarget] =
    React.useState<SkillSummary | null>(null);
  // Track which skill_id (if any) is currently being deleted so we only
  // disable that row's button. Sharing a single mutation across all rows
  // would otherwise disable every row whenever any one delete is in flight.
  const [pendingDeleteSkillId, setPendingDeleteSkillId] = React.useState<
    string | null
  >(null);
  const [pendingDeleteSkill, setPendingDeleteSkill] =
    React.useState<SkillSummary | null>(null);
  const [pendingDeleteVersion, setPendingDeleteVersion] =
    React.useState<VersionDeleteTarget | null>(null);
  // Tracks rows whose visibility toggle is in flight. Set (not single-string)
  // because there's no dialog gating clicks across rows — two concurrent
  // toggles on different chips should each show their own spinner.
  const [pendingVisibilityIds, setPendingVisibilityIds] = React.useState<
    Set<string>
  >(() => new Set());

  const tableState = useDataTableState<SkillSummary>({
    getRowId: (row) => row.skill.skill_id,
    initialSort: { columnId: "name", direction: "asc" },
  });
  const [search, setSearch] = React.useState("");
  // Active filters render as removable chips above the table. Two kinds:
  // ``text`` chips (from Enter-confirming the search field) match the same
  // breadth as live search — name, author, or any tag substring. ``tag``
  // chips (from clicking a tag chip in a row) require an exact tag match.
  // All chips are AND'd together with the live search input.
  const [filters, setFilters] = React.useState<SkillFilter[]>([]);

  function openCreate() {
    setEditingTarget(null);
    setEditorOpen(true);
  }

  const openEdit = React.useCallback(
    (summary: SkillSummary, version: number | null) => {
      setEditingTarget({ summary, version });
      setEditorOpen(true);
    },
    [],
  );

  async function performSkillDelete(): Promise<void> {
    if (pendingDeleteSkill === null) {
      return;
    }
    setPendingDeleteSkillId(pendingDeleteSkill.skill.skill_id);
    try {
      await deleteSkill.mutateAsync(pendingDeleteSkill.skill.skill_id);
    } finally {
      setPendingDeleteSkillId(null);
      setPendingDeleteSkill(null);
    }
  }

  const setPending = React.useCallback(
    (
      setter: React.Dispatch<React.SetStateAction<Set<string>>>,
      skillId: string,
      pending: boolean,
    ) => {
      setter((prev) => {
        const next = new Set(prev);
        if (pending) {
          next.add(skillId);
        } else {
          next.delete(skillId);
        }
        return next;
      });
    },
    [],
  );

  const handleSubscribe = React.useCallback(
    async (summary: SkillSummary) => {
      const skillId = summary.skill.skill_id;
      setPending(setPendingSubscriptionIds, skillId, true);
      try {
        await subscribeSkill.mutateAsync({ skillId });
      } finally {
        setPending(setPendingSubscriptionIds, skillId, false);
      }
    },
    [setPending, subscribeSkill],
  );

  const handleUnsubscribe = React.useCallback(
    async (summary: SkillSummary) => {
      const skillId = summary.skill.skill_id;
      setPending(setPendingSubscriptionIds, skillId, true);
      try {
        await unsubscribeSkill.mutateAsync({ skillId });
      } finally {
        setPending(setPendingSubscriptionIds, skillId, false);
      }
    },
    [setPending, unsubscribeSkill],
  );

  const handleSetAiVersion = React.useCallback(
    async (skillId: string, version: number | null) => {
      setPending(setPendingAiVersionIds, skillId, true);
      try {
        await setSubscription.mutateAsync({
          skillId,
          request: { ai_version: version },
        });
      } finally {
        setPending(setPendingAiVersionIds, skillId, false);
      }
    },
    [setPending, setSubscription],
  );

  const toggleVisibility = React.useCallback(
    async (summary: SkillSummary) => {
      if (summary.skill.created_by !== userId) {
        return;
      }
      const next: SkillAccessibility =
        summary.skill.accessibility === "org" ? "private" : "org";
      const skillId = summary.skill.skill_id;
      setPendingVisibilityIds((prev) => {
        const updated = new Set(prev);
        updated.add(skillId);
        return updated;
      });
      try {
        await updateMetadata.mutateAsync({
          skillId,
          request: { accessibility: next },
        });
      } finally {
        setPendingVisibilityIds((prev) => {
          if (!prev.has(skillId)) {
            return prev;
          }
          const updated = new Set(prev);
          updated.delete(skillId);
          return updated;
        });
      }
    },
    [updateMetadata, userId],
  );

  const allSkills = React.useMemo(
    () => skillsQuery.data?.items ?? [],
    [skillsQuery.data],
  );

  const { resetCurrentPage } = tableState;

  const addFilter = React.useCallback(
    (f: SkillFilter) => {
      const trimmed = f.value.trim();
      if (!trimmed) {
        return;
      }
      const candidate: SkillFilter = { ...f, value: trimmed };
      const key = filterKey(candidate);
      setFilters((prev) =>
        prev.some((existing) => filterKey(existing) === key)
          ? prev
          : [...prev, candidate],
      );
      resetCurrentPage();
    },
    [resetCurrentPage],
  );

  const removeFilter = React.useCallback(
    (key: string) => {
      setFilters((prev) => prev.filter((f) => filterKey(f) !== key));
      resetCurrentPage();
    },
    [resetCurrentPage],
  );

  const clearAllFilters = React.useCallback(() => {
    setFilters([]);
    setSearch("");
    resetCurrentPage();
  }, [resetCurrentPage]);

  // AG Grid has a native quickFilterText option, but the DataTable primitive
  // doesn't expose it yet — until it does, filter client-side here. Skills are
  // already loaded in full, so this is just an in-memory pass.
  const trimmedSearch = search.trim().toLowerCase();
  const filteredSkills = React.useMemo(() => {
    const textNeedles: string[] = [];
    const tagNeedles: string[] = [];
    for (const f of filters) {
      const lowered = f.value.toLowerCase();
      if (f.kind === "tag") {
        tagNeedles.push(lowered);
      } else {
        textNeedles.push(lowered);
      }
    }
    if (trimmedSearch) {
      // Live typed text behaves the same as a confirmed text chip until the
      // user presses Enter — AND'd with everything else.
      textNeedles.push(trimmedSearch);
    }
    if (textNeedles.length === 0 && tagNeedles.length === 0) {
      return allSkills;
    }

    const matchesText = (s: SkillSummary, needle: string): boolean => {
      const name = s.skill.name.toLowerCase();
      const author = s.skill.created_by.toLowerCase();
      const tagSubstr = s.skill.tags.some((t) =>
        t.toLowerCase().includes(needle),
      );
      return name.includes(needle) || author.includes(needle) || tagSubstr;
    };
    const hasTagExact = (s: SkillSummary, needle: string): boolean =>
      s.skill.tags.some((t) => t.toLowerCase() === needle);

    return allSkills.filter((s) => {
      for (const t of textNeedles) {
        if (!matchesText(s, t)) {
          return false;
        }
      }
      for (const t of tagNeedles) {
        if (!hasTagExact(s, t)) {
          return false;
        }
      }
      return true;
    });
  }, [allSkills, filters, trimmedSearch]);

  // The DataTable primitive is wired for server-side sort (its DEFAULT_COL_DEF
  // sets `comparator: () => 0` to inhibit AG Grid's client-side reorder so the
  // server stays authoritative). Skills load in one shot, so we apply the sort
  // ourselves before slicing — column headers still surface the affordance and
  // direction indicator via `sortable: true` on the columns below.
  const sortedSkills = React.useMemo(() => {
    if (tableState.sort === null) {
      return filteredSkills;
    }
    const { columnId, direction } = tableState.sort;
    const direction_factor = direction === "asc" ? 1 : -1;
    const key = (s: SkillSummary): string => {
      switch (columnId) {
        case "name":
          return s.skill.name;
        case "created_by":
          return s.skill.created_by;
        default:
          return "";
      }
    };
    return [...filteredSkills].sort(
      (a, b) =>
        key(a).localeCompare(key(b), undefined, { sensitivity: "base" }) *
        direction_factor,
    );
  }, [filteredSkills, tableState.sort]);

  // Client-side pagination: the skills list is fetched in one shot, but the
  // DataTable footer drives its own page state. Slice locally so prev/next
  // and the "1-N of M" range label behave.
  const start = tableState.currentPage * tableState.pageSize;
  const end = start + tableState.pageSize;
  const visibleSkills = React.useMemo(
    () => sortedSkills.slice(start, end),
    [sortedSkills, start, end],
  );
  const hasNextPage = end < sortedSkills.length;

  const handleSearchChange = React.useCallback(
    (value: string) => {
      setSearch(value);
      // Filter changes the underlying row set — keep prior page index from
      // pointing past the new end of the list.
      resetCurrentPage();
    },
    [resetCurrentPage],
  );

  const handleSearchKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== "Enter") {
        return;
      }
      const trimmed = search.trim();
      if (!trimmed) {
        return;
      }
      addFilter({ kind: "text", value: trimmed });
      setSearch("");
      event.preventDefault();
    },
    [addFilter, search],
  );

  const columns = React.useMemo<ColDef<SkillSummary>[]>(() => {
    const baseColumns: ColDef<SkillSummary>[] = [
      {
        colId: "name",
        headerName: "Name",
        flex: 2,
        sortable: true,
        valueGetter: (p) => p.data?.skill.name ?? "",
      },
      {
        colId: "created_by",
        headerName: "Author",
        flex: 1,
        sortable: true,
        valueGetter: (p) => p.data?.skill.created_by ?? "",
      },
      {
        colId: "visibility",
        headerName: "Visibility",
        width: 130,
        cellRenderer: (p: ICellRendererParams<SkillSummary>) => {
          if (!p.data) {
            return null;
          }
          const isAuthor = p.data.skill.created_by === userId;
          const isPending = pendingVisibilityIds.has(p.data.skill.skill_id);
          const label =
            p.data.skill.accessibility === "org" ? "Org" : "Private";
          const tooltip = isAuthor
            ? `Click to ${
                p.data.skill.accessibility === "org"
                  ? "make private (only you)"
                  : "share with your org"
              }`
            : "Only the author can change visibility";
          return (
            <RobotoTooltip title={tooltip}>
              <span>
                <Chip
                  size="small"
                  label={label}
                  color={
                    p.data.skill.accessibility === "org" ? "primary" : "default"
                  }
                  onClick={
                    isAuthor && !isPending
                      ? () => void toggleVisibility(p.data as SkillSummary)
                      : undefined
                  }
                  className={isAuthor ? styles.visibilityChip : undefined}
                  disabled={!isAuthor || isPending}
                  icon={
                    isPending ? (
                      <CircularProgress
                        size={12}
                        className={styles.visibilityChipSpinner}
                      />
                    ) : undefined
                  }
                />
              </span>
            </RobotoTooltip>
          );
        },
      },
      {
        colId: "tags",
        headerName: "Tags",
        flex: 2,
        sortable: false,
        // AG Grid fires dblclick at the cell level regardless of inner DOM —
        // way more reliable than putting an onDoubleClick on a wrapper div
        // that gets sized to its content (a chip-tall band, or zero on empty
        // cells, both of which we've broken before). Chip-originated dblclicks
        // are filtered out here so a fast double-tap on a tag chip stays a
        // filter action and never escalates into the editor.
        onCellDoubleClicked: (event) => {
          const summary = event.data;
          if (!summary || summary.skill.created_by !== userId) {
            return;
          }
          const target = (event.event?.target ?? null) as HTMLElement | null;
          if (target?.closest(".MuiChip-root")) {
            return;
          }
          setTagsEditTarget(summary);
        },
        cellRenderer: (p: ICellRendererParams<SkillSummary>) => {
          if (!p.data) {
            return null;
          }
          const summary = p.data;
          const tags = summary.skill.tags;
          const isAuthor = summary.skill.created_by === userId;
          let cellTitle: string | undefined;
          if (isAuthor && tags.length > 0) {
            cellTitle = "Click a tag to filter; double-click to edit";
          } else if (isAuthor) {
            cellTitle = "Double-click to add tags";
          }
          return (
            <div className={styles.tagsCell} title={cellTitle}>
              <SkillTagsCell
                tags={tags}
                onTagClick={(tag) => addFilter({ kind: "tag", value: tag })}
              />
            </div>
          );
        },
      },
      {
        colId: "latest",
        headerName: "Latest version",
        width: 140,
        valueGetter: (p) =>
          p.data?.latest_version
            ? `v${p.data.latest_version.version}`
            : "Empty",
      },
      // The AI-version chip only ever applies to rows the caller has subscribed
      // to. On the Org tab every row is unsubscribed-by-definition, so the
      // chip would always be empty there — hide the whole column on that tab.
      ...(tab === "org"
        ? []
        : [
            {
              colId: "ai_indicator",
              headerName: "AI",
              width: 90,
              cellRenderer: (p: ICellRendererParams<SkillSummary>) => {
                if (!p.data) {
                  return null;
                }
                const pinned = p.data.subscription?.ai_version;
                if (pinned === null || pinned === undefined) {
                  return null;
                }
                const latest = p.data.latest_version?.version;
                const isStale = latest !== undefined && latest !== pinned;
                return (
                  <RobotoTooltip
                    title={
                      isStale
                        ? `AI invokes v${pinned} (latest is v${latest}). Switch version in the expanded panel.`
                        : `AI invokes v${pinned} (latest).`
                    }
                  >
                    <Chip
                      size="small"
                      label={`v${pinned}`}
                      variant="outlined"
                      color={isStale ? "warning" : "default"}
                      className={styles.aiPinnedChip}
                    />
                  </RobotoTooltip>
                );
              },
            } satisfies ColDef<SkillSummary>,
          ]),
      {
        colId: "actions",
        headerName: "",
        width: 130,
        sortable: false,
        cellRenderer: (p: ICellRendererParams<SkillSummary>) => {
          if (!p.data) {
            return null;
          }
          const summary = p.data;
          const isAuthor = summary.skill.created_by === userId;
          const subscribed = summary.subscription !== null;
          const subscriptionPending = pendingSubscriptionIds.has(
            summary.skill.skill_id,
          );
          if (!isAuthor && !subscribed) {
            // Org tab: subscribe, no edit / delete affordances. Inspect lives
            // on each version row inside the expanded panel.
            return (
              <div className={styles.actions}>
                <RobotoTooltip title="Subscribe — moves to your Personal tab">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => void handleSubscribe(summary)}
                      disabled={subscriptionPending}
                      loading={subscriptionPending}
                    >
                      Subscribe
                    </Button>
                  </span>
                </RobotoTooltip>
              </div>
            );
          }
          if (!isAuthor) {
            // Personal tab, subscribed-but-not-authored: just Unsubscribe —
            // Inspect is on each version row.
            return (
              <div className={styles.actions}>
                <RobotoTooltip title="Unsubscribe — removes from your Personal tab">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => void handleUnsubscribe(summary)}
                      disabled={subscriptionPending}
                      aria-label="Unsubscribe from skill"
                    >
                      <RemoveCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </RobotoTooltip>
              </div>
            );
          }
          // Author: full edit + delete.
          const isDeleting = pendingDeleteSkillId === summary.skill.skill_id;
          return (
            <div className={styles.actions}>
              <RobotoTooltip title="Edit name, accessibility, and tags">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setMetadataEditTarget(summary)}
                    aria-label="Edit skill"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </RobotoTooltip>
              <RobotoTooltip title="Delete the entire skill (permanent)">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setPendingDeleteSkill(summary)}
                    disabled={isDeleting}
                    aria-label="Delete skill"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </RobotoTooltip>
            </div>
          );
        },
      },
    ];
    // DataTable's DEFAULT_COL_DEF hides columns by default (the
    // column-visibility picker is the source of truth for opt-in consumers).
    // Skills doesn't use that system — every column is meant to be visible —
    // so flip the default. Spread order lets any column still opt out by
    // setting `hide: true` explicitly.
    return baseColumns.map((c) => ({ hide: false, ...c }));
  }, [
    addFilter,
    handleSubscribe,
    handleUnsubscribe,
    pendingDeleteSkillId,
    pendingSubscriptionIds,
    pendingVisibilityIds,
    tab,
    toggleVisibility,
    userId,
  ]);

  let loading: DataTableLoadingState = "idle";
  if (orgId === undefined || skillsQuery.isLoading) {
    loading = "foreground";
  } else if (skillsQuery.isFetching) {
    loading = "background";
  }

  const renderVersionsPanel = React.useCallback(
    (summary: SkillSummary) => (
      <div className={styles.expandedContainer}>
        <VersionsSubTable
          summary={summary}
          isAuthor={summary.skill.created_by === userId}
          subscribed={summary.subscription !== null}
          aiPinnedVersion={summary.subscription?.ai_version ?? null}
          aiVersionPending={pendingAiVersionIds.has(summary.skill.skill_id)}
          onSetAiVersion={(version) =>
            void handleSetAiVersion(summary.skill.skill_id, version)
          }
          onInspectVersion={(version) => setInspectTarget({ summary, version })}
          onEditVersion={(version) => openEdit(summary, version)}
          onRequestDeleteVersion={(version, isLast) =>
            setPendingDeleteVersion({
              summary,
              version,
              isLastVersion: isLast,
            })
          }
        />
      </div>
    ),
    [handleSetAiVersion, openEdit, pendingAiVersionIds, userId],
  );

  return (
    <div className={styles.page}>
      <div className={styles.headerRow}>
        <Typography variant="h6">Skills</Typography>
        <Button variant="contained" onClick={openCreate}>
          New skill
        </Button>
      </div>

      <Typography variant="body2" className={styles.intro}>
        Skills are versioned procedures the AI chat can apply. Make a skill
        org-shared to make it available to your teammates, or keep it private to
        iterate on your own. Expand a skill to manage its versions.
      </Typography>

      {skillsQuery.isError && (
        <Alert severity="error" className={styles.errorAlert}>
          Failed to load skills.
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, next: SkillListScope) => {
          setTab(next);
          resetCurrentPage();
        }}
        className={styles.tabs}
      >
        <Tab value="personal" label="Personal" />
        <Tab value="org" label="Org" />
      </Tabs>

      <div className={styles.toolbar}>
        <TextField
          size="small"
          placeholder="Search by name, author, or tag"
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          className={styles.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </div>
      {filters.length > 0 && (
        <div className={styles.filtersRow}>
          {filters.map((f) => {
            const key = filterKey(f);
            const label = f.kind === "tag" ? `tag: ${f.value}` : `"${f.value}"`;
            return (
              <Chip
                key={key}
                size="small"
                label={label}
                color={f.kind === "tag" ? "primary" : "default"}
                onDelete={() => removeFilter(key)}
              />
            );
          })}
          {filters.length > 1 && (
            <button
              type="button"
              className={styles.clearAllFilters}
              onClick={clearAllFilters}
            >
              Clear all
            </button>
          )}
        </div>
      )}

      <div className={styles.tableWrapper}>
        <DataTable.Root>
          <DataTable.Table<SkillSummary>
            {...tableState.table}
            hasNextPage={hasNextPage}
            loading={loading}
            columns={columns}
            data={visibleSkills}
            totalRowCount={filteredSkills.length}
            noRowsContent={
              allSkills.length === 0 ? (
                <span>
                  No skills yet. Click <strong>New skill</strong> to create one.
                </span>
              ) : (
                <span>No skills match the current filters.</span>
              )
            }
            expansion={{ renderRow: renderVersionsPanel }}
          />
        </DataTable.Root>
      </div>

      <SkillEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        summary={editingTarget?.summary ?? null}
        editingVersion={editingTarget?.version ?? null}
        orgId={orgId}
      />

      <SkillMetadataEditor
        open={metadataEditTarget !== null}
        onClose={() => setMetadataEditTarget(null)}
        summary={metadataEditTarget}
        orgId={orgId}
      />

      <TagsEditor
        open={tagsEditTarget !== null}
        onClose={() => setTagsEditTarget(null)}
        summary={tagsEditTarget}
        orgId={orgId}
      />

      <SkillInspector
        open={inspectTarget !== null}
        onClose={() => setInspectTarget(null)}
        summary={inspectTarget?.summary ?? null}
        initialVersion={inspectTarget?.version ?? null}
      />

      <AlertDialog
        dialogOpen={pendingDeleteSkill !== null}
        handleClose={() => {
          if (pendingDeleteSkillId === null) {
            setPendingDeleteSkill(null);
          }
        }}
        dialogTitle={
          pendingDeleteSkill
            ? `Delete skill "${pendingDeleteSkill.skill.name}"?`
            : "Delete skill"
        }
        dialogContent={
          <div className={styles.deleteWarning}>
            <span className={styles.deleteWarningEmphasis}>
              This is permanent.
            </span>
            <span>
              The skill and all of its versions will be removed. Existing chats
              keep any skill_invocation messages they&apos;ve already produced —
              the body is captured at invocation time. You can re-create a skill
              with the same name afterwards.
            </span>
          </div>
        }
        dialogAction={performSkillDelete}
      />

      <DeleteVersionDialog
        target={pendingDeleteVersion}
        orgId={orgId}
        onClose={() => setPendingDeleteVersion(null)}
      />
    </div>
  );
}

const MAX_VISIBLE_TAGS = 2;

interface SkillTagsCellProps {
  tags: string[];
  /** Optional click handler — when set, each chip becomes a filter trigger. */
  onTagClick?: (tag: string) => void;
}

function SkillTagsCell({ tags, onTagClick }: SkillTagsCellProps) {
  const sorted = [...tags].sort();
  const visible = sorted.slice(0, MAX_VISIBLE_TAGS);
  const overflowCount = sorted.length - visible.length;
  return (
    <>
      {visible.map((tag) => {
        // Same deterministic color hash as the shared <Tag> renders elsewhere
        // — keyed off the tag string, with a small "error/warn/success" bias.
        // 35% alpha for the rest state, 55% on hover, mirroring shared/Tags.
        const rgb = assignTagColor(tag).join(", ");
        return (
          <Chip
            key={tag}
            size="small"
            label={tag}
            onClick={
              onTagClick
                ? (e) => {
                    // Stop the dblclick from bubbling to the cell — clicking
                    // a chip should only ever add a filter, never open the
                    // editor.
                    e.stopPropagation();
                    onTagClick(tag);
                  }
                : undefined
            }
            clickable={onTagClick !== undefined}
            sx={{
              backgroundColor: `rgba(${rgb}, 0.35)`,
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: `rgba(${rgb}, 0.55)`,
              },
            }}
          />
        );
      })}
      {overflowCount > 0 && (
        <span
          className={styles.tagOverflow}
          title={sorted.slice(MAX_VISIBLE_TAGS).join(", ")}
        >
          +{overflowCount} more
        </span>
      )}
    </>
  );
}

interface VersionsSubTableProps {
  summary: SkillSummary;
  isAuthor: boolean;
  /** True when the caller has a subscription row (always true for authors). */
  subscribed: boolean;
  /** Currently pinned version for the caller's AI, or null if not exposed to AI. */
  aiPinnedVersion: number | null;
  /** True while a setSubscription mutation is in flight for this skill. */
  aiVersionPending: boolean;
  onSetAiVersion: (version: number | null) => void;
  onInspectVersion: (version: number) => void;
  onEditVersion: (version: number) => void;
  onRequestDeleteVersion: (version: number, isLast: boolean) => void;
}

/**
 * Per-version table rendered inside an expanded skill row. Each row shows:
 *
 * - the version number,
 * - the "when to use" text,
 * - an **Available to AI** Switch wired to the caller's subscription
 *   ``ai_version`` (at most one version per skill can be on — flipping a
 *   second version takes the pin from the previously-on row),
 * - and either Edit / Delete (author) or Inspect (subscriber) actions.
 */
function VersionsSubTable({
  summary,
  isAuthor,
  subscribed,
  aiPinnedVersion,
  aiVersionPending,
  onSetAiVersion,
  onInspectVersion,
  onEditVersion,
  onRequestDeleteVersion,
}: VersionsSubTableProps) {
  const versionsQuery = useSkillVersions(summary.skill.skill_id);

  const versions = versionsQuery.data ?? [];

  if (versionsQuery.isLoading) {
    return <div className={styles.versionEmpty}>Loading versions…</div>;
  }
  if (versions.length === 0) {
    return <div className={styles.versionEmpty}>No versions yet.</div>;
  }

  return (
    <Table size="small" className={styles.versionsTable}>
      <TableHead>
        <TableRow>
          <TableCell width={80}>Version</TableCell>
          <TableCell>When to use</TableCell>
          {subscribed && (
            <TableCell width={120} align="center">
              Available to AI
            </TableCell>
          )}
          <TableCell width={120} align="right">
            Actions
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {versions.map((version) => {
          const isLastVersion = versions.length === 1;
          const isPinned = aiPinnedVersion === version.version;
          let aiTooltip: string;
          if (isPinned) {
            aiTooltip = `AI invokes this version. Click to disable.`;
          } else if (aiPinnedVersion !== null) {
            aiTooltip = `Click to switch the AI pin from v${aiPinnedVersion} to v${version.version}.`;
          } else {
            aiTooltip = `Click to pin v${version.version} for AI auto-invocation.`;
          }
          let deleteTooltip = "Only the author can delete";
          if (isAuthor) {
            deleteTooltip = isLastVersion
              ? "Deleting the only version will also delete the skill"
              : `Delete v${version.version}`;
          }
          return (
            <TableRow key={version.version}>
              <TableCell>v{version.version}</TableCell>
              <TableCell>
                <div
                  className={styles.versionDescription}
                  title={version.description}
                >
                  {version.description}
                </div>
              </TableCell>
              {subscribed && (
                <TableCell align="center">
                  <RobotoTooltip title={aiTooltip}>
                    <span>
                      <IconButton
                        size="small"
                        color={isPinned ? "primary" : "default"}
                        onClick={() =>
                          onSetAiVersion(isPinned ? null : version.version)
                        }
                        disabled={aiVersionPending}
                        aria-label={`Available to AI for ${summary.skill.name} v${version.version}`}
                        aria-pressed={isPinned}
                      >
                        {isPinned ? (
                          <PushPinIcon fontSize="small" />
                        ) : (
                          <PushPinOutlinedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </RobotoTooltip>
                </TableCell>
              )}
              <TableCell align="right">
                <div className={styles.actions}>
                  {isAuthor ? (
                    <>
                      <RobotoTooltip title={`Edit v${version.version}`}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onEditVersion(version.version)}
                            aria-label={`Edit v${version.version}`}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </RobotoTooltip>
                      <RobotoTooltip title={deleteTooltip}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() =>
                              onRequestDeleteVersion(
                                version.version,
                                isLastVersion,
                              )
                            }
                            aria-label={`Delete v${version.version}`}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </RobotoTooltip>
                    </>
                  ) : (
                    <RobotoTooltip title={`Inspect v${version.version}`}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => onInspectVersion(version.version)}
                          aria-label={`Inspect v${version.version}`}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </RobotoTooltip>
                  )}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

interface DeleteVersionDialogProps {
  target: VersionDeleteTarget | null;
  orgId: string | undefined;
  onClose: () => void;
}

function DeleteVersionDialog({
  target,
  orgId,
  onClose,
}: DeleteVersionDialogProps) {
  const deleteVersion = useDeleteVersion(orgId);

  async function performDelete(): Promise<void> {
    if (target === null) {
      return;
    }
    try {
      await deleteVersion.mutateAsync({
        skillId: target.summary.skill.skill_id,
        version: target.version,
      });
    } finally {
      onClose();
    }
  }

  const title = target
    ? `Delete version v${target.version} of "${target.summary.skill.name}"?`
    : "Delete version";

  return (
    <AlertDialog
      dialogOpen={target !== null}
      handleClose={() => {
        if (!deleteVersion.isPending) {
          onClose();
        }
      }}
      dialogTitle={title}
      dialogContent={
        <div className={styles.deleteWarning}>
          <span className={styles.deleteWarningEmphasis}>
            This is permanent.
          </span>
          <span>
            {target?.isLastVersion
              ? "This is the only remaining version. Deleting it will also remove the skill itself."
              : "Existing chats keep any skill_invocation messages they've already produced — the body is captured at invocation time."}
          </span>
        </div>
      }
      dialogAction={performDelete}
    />
  );
}
